import {
  ColumnBlockModel,
  ColumnRowBlockModel,
  NoteBlockModel,
} from '@blocksuite/affine-model';
import type { BlockModel, Store } from '@blocksuite/store';

export type ColumnSide = 'left' | 'right';

/** Resolve nested list/text targets to the block occupying this part of a row. */
export function getColumnTarget(store: Store, target: BlockModel) {
  let block: BlockModel | null = target;
  while (block) {
    const parent = store.getParent(block);
    if (
      parent instanceof NoteBlockModel ||
      parent instanceof ColumnBlockModel
    ) {
      return block;
    }
    block = parent;
  }
  return null;
}

export function canCreateColumn(
  store: Store,
  target: BlockModel,
  blocks: BlockModel[]
) {
  const anchor = getColumnTarget(store, target);
  if (store.readonly || !anchor || !blocks.length) return false;
  if (anchor instanceof ColumnRowBlockModel) return false;
  if (!store.schema.safeValidate(anchor.flavour, 'affine:column')) return false;
  return blocks.every(block => {
    if (!store.schema.safeValidate(block.flavour, 'affine:column'))
      return false;
    // A move must never put an ancestor inside its own descendant.
    let current: BlockModel | null = anchor;
    while (current) {
      if (current.id === block.id) return false;
      current = store.getParent(current);
    }
    return true;
  });
}

/** Remove only empty layout containers; unwrap a row with one remaining column. */
export function normalizeColumnRows(store: Store) {
  if (store.readonly) return;
  const rows = store.getBlocksByFlavour('affine:column-row').map(b => b.model);
  for (const row of rows.reverse()) {
    if (!store.getBlock(row.id)) continue;
    for (const column of [...row.children]) {
      if (!column.children.length) store.deleteBlock(column);
    }
    if (row.children.length > 1) continue;
    const parent = store.getParent(row);
    if (!parent) continue;
    const column = row.children[0];
    if (column) {
      // Move separately: multi-selection need not be contiguous after editing.
      for (const child of [...column.children]) {
        store.moveBlocks([child], parent, row);
      }
      store.deleteBlock(column);
    }
    store.deleteBlock(row);
  }
}

/** Preserve IDs, text objects and descendants in a single undo group. */
export function moveBlocksToColumn(
  store: Store,
  target: BlockModel,
  blocks: BlockModel[],
  side: ColumnSide
) {
  if (!canCreateColumn(store, target, blocks)) return null;
  const anchor = getColumnTarget(store, target)!;
  const parent = store.getParent(anchor)!;
  let columnId: string;
  store.captureSync();
  if (parent instanceof ColumnBlockModel) {
    const row = store.getParent(parent)!;
    columnId = store.addBlock(
      'affine:column',
      { width: parent.props.width },
      row,
      row.children.indexOf(parent) + (side === 'right' ? 1 : 0)
    );
  } else {
    const rowId = store.addBlock(
      'affine:column-row',
      {},
      parent,
      parent.children.indexOf(anchor)
    );
    const anchorColumnId = store.addBlock('affine:column', {}, rowId);
    columnId = store.addBlock(
      'affine:column',
      {},
      rowId,
      side === 'left' ? 0 : 1
    );
    store.moveBlocks([anchor], store.getBlock(anchorColumnId)!.model);
  }
  const column = store.getBlock(columnId)!.model;
  for (const block of blocks) store.moveBlocks([block], column);
  normalizeColumnRows(store);
  store.captureSync();
  return columnId;
}
